import React, { useEffect, useMemo, useState } from "react";
import { Container } from "reactstrap";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/CommonElements/PageTitle";
import { TableC } from "@/CommonElements/TableC";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { useGetGroupPilgrimsInquiryQuery } from "@/services/makkahGroups";
import { LabelValue } from "@/Component/Value";
import { Tooltip } from "antd";
import { InformationCircleIcon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useMissionCompaniesQuery, usePilgrimGroupNames } from "@/services/basicData/basicData";
import { useDebouncedSearch } from "@/utils/hooks/useDebouncedSearch";

const PAGE_SIZE = 15;

const GroupPilgrimsQuery = () => {
  const { t } = useTranslation();
  const { getLocalizedText } = useLocalizedText();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({});
  const [selectedMissionId, setSelectedMissionId] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const { searchValue, debouncedValue, setSearchValue } = useDebouncedSearch("", 500);

  useEffect(() => {
    setSelectedMissionId(filters.mission_id ?? null);
    setSelectedCompanyId(filters.company_id ?? null);
  }, [filters.mission_id, filters.company_id]);

  const apiParams = {
    page,
    per_page: PAGE_SIZE,
    search: debouncedValue || undefined,
    group_name: filters.group_name,
    campaign_number: filters.group_number,
    mission_id: filters.mission_id,
    company_id: filters.company_id,
  };

  const { data: response, isLoading } = useGetGroupPilgrimsInquiryQuery(apiParams);
  const { data: missionCompaniesRes, isLoading: isMissionCompaniesLoading } = useMissionCompaniesQuery(selectedMissionId);
  const missionCompanies = missionCompaniesRes?.data?.data?.companies ?? [];

  const missionIdForGroupNames = selectedMissionId ?? filters.mission_id;
  const companyIdForGroupNames = selectedCompanyId ?? filters.company_id;
  const { data: pilgrimGroupNamesRes, isLoading: isLoadingPilgrimGroupNames } = usePilgrimGroupNames({
    mission_id: missionIdForGroupNames,
    company_id: companyIdForGroupNames,
  });
  const rawGroupNamesOptions = useMemo(() => {
    const list = pilgrimGroupNamesRes?.data?.data ?? [];
    return (Array.isArray(list) ? list : []).map((m) => ({
      value: m.raw_group_name,
      label: getLocalizedText(m.raw_group_name_ar, m.raw_group_name_en) ?? m.raw_group_name ?? "",
    }));
  }, [pilgrimGroupNamesRes, getLocalizedText]);

  const data = response?.data?.data ?? [];
  const meta = response?.data?.meta ?? {};

  useEffect(() => {
    setPage(1);
  }, [filters, debouncedValue]);

  const columns = [
    {
      name: t("BUILDING_ORGANIZER"),
      key: "organizer",
      render: (_, row) =>
        getLocalizedText(row?.organizer_ar, row?.organizer_en) || "-",
    },
    {
      name: t("AFFILIATED_ORGANIZER"),
      key: "sub_organizer",
      render: (_, row) =>
        getLocalizedText(row?.sub_organizer_ar, row?.sub_organizer_en) || "-",
    },
    {
      name: t("GROUP_NUMBER"),
      key: "group_number",
      render: (value) => (
        <LabelValue value={value ?? "-"} copyValue={value} buttonIconSize={12} />
      ),
    },
    {
      name: t("GROUP_NAME"),
      key: "group_name",
      render: (_, row) =>
        getLocalizedText(row?.group_name_ar, row?.group_name_en) || "-",
    },
    {
      name: t("MAKKAH_GROUPS_COL_TOTAL_PILGRIMS"),
      key: "pilgrim_count",
      render: (value) => value ?? "-",
    },
  ];

  const filterFields = [
    {
      name: "mission_id",
      label: t("BUILDING_ORGANIZER"),
      type: "select",
      placeholder: t("DELEGATION"),
      optionKey: "missions",
      options: (mission) => ({
        label: getLocalizedText(mission.name_ar, mission.name_en),
        value: mission.id,
      }),
      onChange: (value) => {
        setSelectedMissionId(value ?? null);
      },
    },
    {
      name: "company_id",
      label: t("AFFILIATED_ORGANIZER"),
      type: "select",
      disabled: !selectedMissionId,
      loading: selectedMissionId ? isMissionCompaniesLoading : false,
      placeholder: t("AFFILIATED_ORGANIZER"),
      options: selectedMissionId
        ? missionCompanies.map((c) => ({
            value: c.id,
            label: getLocalizedText(c.companyNameAr, c.companyNameEn) || getLocalizedText(c.name_ar, c.name_en),
          }))
        : [],
      onChange: (value) => setSelectedCompanyId(value ?? null),
    },
    {
      colSize: { md: 6 },
      name: "group_name",
      label: t("GROUP_NAME"),
      type: "select",
      placeholder: t("GROUP_NAME_PLACEHOLDER"),
      // disabled: !missionIdForGroupNames || !companyIdForGroupNames,
      loading: missionIdForGroupNames && companyIdForGroupNames ? isLoadingPilgrimGroupNames : false,
      options: rawGroupNamesOptions,
    },
    {
      colSize: { md: 6 },
      name: "campaign_number",
      label: t("CAMPAIGN_NUMBER"),
      type: "input",
      placeholder: t("CAMPAIGN_NUMBER_PLACEHOLDER"),
    },
  ];

  const handleFilterApply = (filterData) => {
    setFilters(filterData);
  };

  const handleFilterReset = () => {
    setFilters({});
  };

  const headerTitle = (
    <span className="d-flex align-items-center gap-2">
      {t("GROUP_PILGRIMS_QUERY")}
      <Tooltip title={t("GROUP_PILGRIMS_QUERY")}>
        <span className="text-muted">
          <HugeiconsIcon size={18} icon={InformationCircleIcon} />
        </span>
      </Tooltip>
    </span>
  );

  return (
    <div>
      <PageHeader title={headerTitle} sticky={false} />
      <Container fluid>
        <TableC
          data={data}
          columns={columns}
          loading={isLoading}
          searchValue={searchValue}
          onSearchChange={(e) => setSearchValue(e.target.value)}
          searchPlaceholder={t("SEARCH_HERE")}
          pagination={{
            current: meta?.current_page ?? page,
            total: meta?.total ?? 0,
            pageSize: meta?.per_page ?? PAGE_SIZE,
            onChange: setPage,
          }}
          filterFields={filterFields}
          filters={filters}
          onFilterApply={handleFilterApply}
          onFilterReset={handleFilterReset}
        />
        {meta?.total_pilgrims != null && !isLoading && (
          <div className="mt-2 text-muted small">
            {t("TOTAL_PILGRIMS")}: {meta.total_pilgrims}
          </div>
        )}
      </Container>
    </div>
  );
};

export default GroupPilgrimsQuery;
